// Auto-explainer footnotes for the perf-v2 page (#4306).
//
// Each narrator takes the same data the chart/table already renders and
// returns a one-sentence summary (or null when nothing useful to say).
// Pure functions — no LLM calls, no I/O — so they fire on every render
// without round-trip cost.
//
// Tone follows the team's weekly client reports: descriptive, surfaces
// the top entry + extremes, notes exclusions. No hedging, no "consider".

import type { AccountSearchTerm, CampaignStatusData, ConversionActionRow, DemographicRow } from '@/lib/apis';

const formatPct = (v: number, dp = 1): string => `${v.toFixed(dp)}%`;

// ----------------------------------------------------------------------------
// Demographics
// ----------------------------------------------------------------------------

/**
 * Demographics narrator. Identifies the dominant segment by impressions,
 * the segment with the best CTR, and notes any "Undetermined"/"Unknown"
 * exclusion — matching the example narrative in the ticket.
 */
export function explainDemographics(
  rows: DemographicRow[],
  dimension: 'age' | 'gender' | 'parental_status',
): string | null {
  if (!rows || rows.length === 0) return null;

  // Aggregate across campaigns by segment so the narrator reads at the
  // dimension level, not the per-row campaign level.
  const bySegment = new Map<string, { impressions: number; clicks: number }>();
  for (const r of rows) {
    if (!r.segment) continue;
    const existing = bySegment.get(r.segment) ?? { impressions: 0, clicks: 0 };
    existing.impressions += r.impressions;
    existing.clicks += r.clicks;
    bySegment.set(r.segment, existing);
  }
  if (bySegment.size === 0) return null;

  const isUnknownSegment = (seg: string): boolean =>
    seg === 'UNDETERMINED' || seg === 'AGE_RANGE_UNDETERMINED' || seg === 'UNKNOWN';

  const known = Array.from(bySegment.entries()).filter(([s]) => !isUnknownSegment(s));
  if (known.length === 0) return null;

  const totalKnownClicks = known.reduce((s, [, v]) => s + v.clicks, 0);
  const totalUnknownClicks = Array.from(bySegment.entries())
    .filter(([s]) => isUnknownSegment(s))
    .reduce((s, [, v]) => s + v.clicks, 0);

  // Pretty-print enum names to match the table's UI labels.
  const prettySegment = (seg: string): string => {
    const m = seg.match(/^AGE_RANGE_(\d+)_(\d+)$/);
    if (m) return `${m[1]}-${m[2]}`;
    if (seg === 'AGE_RANGE_65_UP') return '65+';
    if (seg === 'NOT_A_PARENT') return 'non-parents';
    return seg.charAt(0) + seg.slice(1).toLowerCase().replace(/_/g, ' ');
  };

  // Dominant segment by clicks.
  const dominant = known.reduce((best, curr) => (curr[1].clicks > best[1].clicks ? curr : best));
  const dominantPct = totalKnownClicks > 0 ? (dominant[1].clicks / totalKnownClicks) * 100 : 0;

  // Best CTR.
  const ctrEntries = known
    .filter(([, v]) => v.impressions >= 20) // ignore very thin segments
    .map(([s, v]) => [s, v.impressions > 0 ? (v.clicks / v.impressions) * 100 : 0] as const);
  const bestCtr = ctrEntries.length > 0
    ? ctrEntries.reduce((best, curr) => (curr[1] > best[1] ? curr : best))
    : null;

  const segmentNoun = dimension === 'age' ? 'age group' : dimension === 'gender' ? 'gender' : 'parental status';

  let sentence = `${prettySegment(dominant[0])} is the dominant ${segmentNoun} at ${formatPct(dominantPct, 1)} of known-${dimension === 'parental_status' ? 'status' : dimension} clicks`;

  if (bestCtr && bestCtr[0] !== dominant[0]) {
    sentence += `; ${prettySegment(bestCtr[0])} has the highest CTR at ${formatPct(bestCtr[1], 1)}`;
  } else if (bestCtr) {
    sentence += `, with the highest CTR at ${formatPct(bestCtr[1], 1)}`;
  }
  sentence += '.';

  if (totalUnknownClicks > 0) {
    sentence += ` Note: ${totalUnknownClicks.toLocaleString()} click${totalUnknownClicks === 1 ? '' : 's'} from unknown ${segmentNoun}s are excluded from these percentages.`;
  }
  return sentence;
}

// ----------------------------------------------------------------------------
// Budget Utilisation
// ----------------------------------------------------------------------------

/**
 * Budget table narrator. Picks out campaigns at/over 100% (saturated) and
 * those with high Search Lost IS (budget) — the actionable subset.
 */
export interface BudgetUtilisationRowLike {
  name: string;
  utilisation: number;
  googleadsCampaignId?: string | null;
}

export function explainBudgetUtilisation(
  rows: BudgetUtilisationRowLike[],
  statusByGoogleId: Map<string, CampaignStatusData>,
): string | null {
  if (!rows || rows.length === 0) return null;

  const saturated = rows.filter(r => r.utilisation >= 100);
  const limited = rows.filter(r => {
    if (!r.googleadsCampaignId) return false;
    const s = statusByGoogleId.get(r.googleadsCampaignId);
    return s?.primary_status === 'LIMITED' && (s?.search_budget_lost_impression_share ?? 0) >= 0.3;
  });

  if (saturated.length === 0 && limited.length === 0) {
    return `${rows.length} campaign${rows.length === 1 ? '' : 's'} are spending within their daily budgets.`;
  }

  const parts: string[] = [];
  if (saturated.length > 0) {
    const names = saturated.slice(0, 2).map(r => `"${r.name}"`).join(' and ');
    const more = saturated.length > 2 ? ` and ${saturated.length - 2} other${saturated.length - 2 === 1 ? '' : 's'}` : '';
    parts.push(`${names}${more} ${saturated.length === 1 ? 'is' : 'are'} at full daily-budget utilisation`);
  }
  if (limited.length > 0) {
    const top = limited.reduce((best, curr) => {
      const bs = statusByGoogleId.get(curr.googleadsCampaignId!)?.search_budget_lost_impression_share ?? 0;
      const cs = statusByGoogleId.get(best.googleadsCampaignId!)?.search_budget_lost_impression_share ?? 0;
      return bs > cs ? curr : best;
    });
    const lostIs = statusByGoogleId.get(top.googleadsCampaignId!)?.search_budget_lost_impression_share ?? 0;
    parts.push(`"${top.name}" is losing ${formatPct(lostIs * 100, 0)} of impressions to budget — the lever most likely to unlock reach`);
  }
  return parts.join('. ') + '.';
}

// ----------------------------------------------------------------------------
// Search Impression Share (campaign-level snapshot)
// ----------------------------------------------------------------------------

/**
 * Campaign-table narrator focused on IS. Surfaces the campaign with the
 * lowest IS and the campaign with the most budget-lost IS so users see
 * where the gap is + why it's there.
 */
export function explainImpressionShare(
  campaigns: CampaignStatusData[],
): string | null {
  const withIs = campaigns.filter(c => c.search_impression_share != null && c.impressions > 0);
  if (withIs.length === 0) return null;

  const lowestIs = withIs.reduce((best, curr) =>
    (curr.search_impression_share ?? 1) < (best.search_impression_share ?? 1) ? curr : best,
  );
  const lowestIsPct = (lowestIs.search_impression_share ?? 0) * 100;

  const budgetLossers = withIs.filter(c => (c.search_budget_lost_impression_share ?? 0) >= 0.3);
  const rankLossers = withIs.filter(c => (c.search_rank_lost_impression_share ?? 0) >= 0.3);

  let sentence = `"${lowestIs.campaign_name}" has the lowest Search IS at ${formatPct(lowestIsPct, 1)}.`;
  if (budgetLossers.length > 0 && rankLossers.length === 0) {
    sentence += ` ${budgetLossers.length} campaign${budgetLossers.length === 1 ? '' : 's'} losing ≥30% of impressions to budget — increasing spend would help.`;
  } else if (rankLossers.length > 0 && budgetLossers.length === 0) {
    sentence += ` ${rankLossers.length} campaign${rankLossers.length === 1 ? '' : 's'} losing ≥30% of impressions to Ad Rank — bid + Quality Score work would help.`;
  } else if (budgetLossers.length > 0 && rankLossers.length > 0) {
    sentence += ` Budget loss dominates for ${budgetLossers.length} campaign${budgetLossers.length === 1 ? '' : 's'}; rank loss dominates for ${rankLossers.length}.`;
  }
  return sentence;
}

// ----------------------------------------------------------------------------
// Conversion Mix
// ----------------------------------------------------------------------------

/**
 * Conversion-mix narrator. Names the top action by count and notes its
 * share of total conversions — matching the team's reports.
 */
export function explainConversionMix(
  rows: ConversionActionRow[],
): string | null {
  if (!rows || rows.length === 0) return null;

  const byAction = new Map<string, number>();
  for (const r of rows) {
    const name = r.conversion_action_name || 'Unknown';
    byAction.set(name, (byAction.get(name) ?? 0) + r.conversions);
  }
  const total = Array.from(byAction.values()).reduce((s, v) => s + v, 0);
  if (total === 0) return null;

  const sorted = Array.from(byAction.entries()).sort(([, a], [, b]) => b - a);
  const [topName, topCount] = sorted[0];
  const topPct = (topCount / total) * 100;

  const sentence = `"${topName}" accounts for ${formatPct(topPct, 0)} of ${total.toFixed(0)} conversions this period`;
  if (sorted.length > 1) {
    const [secondName, secondCount] = sorted[1];
    return `${sentence}; "${secondName}" follows with ${secondCount.toFixed(0)}.`;
  }
  return `${sentence}.`;
}

// ----------------------------------------------------------------------------
// Search Terms preview
// ----------------------------------------------------------------------------

/**
 * Search-terms narrator. Counts how many of the visible terms still have
 * NONE status (i.e. actionable — neither added nor excluded).
 */
export function explainSearchTerms(rows: AccountSearchTerm[]): string | null {
  if (!rows || rows.length === 0) return null;
  const total = rows.length;
  const none = rows.filter(r => !r.status || r.status === 'NONE' || r.status === 'UNKNOWN').length;
  const excluded = rows.filter(r => r.status === 'EXCLUDED').length;
  if (none === 0 && excluded === 0) return null;
  const parts: string[] = [];
  if (none > 0) parts.push(`${none.toLocaleString()} of ${total.toLocaleString()} term${total === 1 ? '' : 's'} ${none === 1 ? 'is' : 'are'} not yet added or excluded — still actionable`);
  if (excluded > 0) parts.push(`${excluded.toLocaleString()} already on a negative list`);
  return parts.join('; ') + '.';
}
